'use client';

import * as DialogComponent from '@radix-ui/react-dialog';
import type { PropsWithChildren, ReactNode } from 'react';
import { tv } from '../_lib/utils';
import { Button } from '../Inputs/Button';
import { type Brands } from '../Theme';
import CloseIcon from '../_assets/icons/close.svg';

const styles = tv({
	slots: {
		base: 'bottom-0 left-1/2 fixed top-1/2 -translate-x-1/2 -translate-y-1/2 h-fit pointer-events-none! data-[state=open]:animate-popup-modal-open data-[state=closed]:animate-popup-modal-closed',
		overlay:
			'bg-purple-100/80 h-[100dvh] left-0 fixed top-0 w-screen data-[state=open]:animate-popup-overlay-open data-[state=closed]:animate-popup-overlay-closed',
		modal: '@container bg-background-primary rounded-lg overflow-auto w-full relative p-6 pointer-events-auto font-poppins',
		close: 'z-20',
	},
	variants: {
		layout: {
			sidebar: {
				base: 'no-nested-containers h-[100dvh] top-0 right-0 translate-x-0 translate-y-0 p-2 left-auto w-full md:p-4 md:max-w-max-sm data-[state=open]:animate-popup-sidebar-modal-open data-[state=closed]:animate-popup-sidebar-modal-closed',
				modal: 'h-full max-h-full w-full py-8 px-5 md:px-10',
				close: 'flex justify-end sticky top-0 mb-3 lg:-mb-4',
			},
			'sidebar-slim': {
				base: 'no-nested-containers max-w-[40rem] h-[100dvh] top-0 right-0 translate-x-0 translate-y-0 p-2 left-auto w-full md:p-4 data-[state=open]:animate-popup-sidebar-modal-open data-[state=closed]:animate-popup-sidebar-modal-closed',
				modal: 'h-full max-h-full w-full p-responsive-xl pt-responsive-sm',
				close: 'flex justify-end sticky top-0 lg:-mr-6',
			},
			search: {
				base: 'container-sm [--container-max-width:45rem] w-full',
				modal: '',
				close: 'flex justify-end sticky top-0',
			},
			video: {
				base: 'h-[100dvh] container-xl w-full',
				modal: 'p-0 aspect-video w-auto left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 max-h-[90%]',
				close: 'absolute top-[1rem] right-[1rem]',
			},
			modal: {
				base: 'w-full px-5',
				modal: 'bg-transparent max-w-5xl p-0 mx-auto',
				close: 'hidden',
			},
			product: {
				base: 'w-full top-0 left-0 translate-none h-auto data-[state=open]:animate-popup-product-modal-open data-[state=closed]:animate-popup-product-modal-closed',
				overlay: 'bg-white/30 backdrop-blur-2xl',
				modal:
					'pointer-events-none bg-transparent rounded-none m-0 mx-auto overflow-auto max-h-full px-4 py-8 md:px-10 md:py-20 [scrollbar-width:none]',
				close: 'pointer-events-auto fixed bottom-12 left-1/2 -translate-x-1/2 z-60',
			},
		},
		nestedPopup: {
			true: {
				base: 'z-80',
				overlay: 'z-70',
			},
			false: {
				base: 'z-70',
				overlay: 'z-60',
			},
		},
	},
});

export type PopupProps = {
	brand?: Brands;
	className?: string;
	layout?: 'sidebar' | 'sidebar-slim' | 'search' | 'video' | 'modal' | 'product';
	triggerButton?: any;
	open?: boolean;
	setOpen?: Function;
	nestedPopup?: boolean;
	closeButton?: ReactNode;
};

export function Popup(props: PropsWithChildren<PopupProps>) {
	const brand = props.brand ?? 'link';
	const layout = props.layout || 'sidebar';
	const nestedPopup = props.nestedPopup ?? false;
	const { base, overlay, modal, close } = styles({ layout, nestedPopup });

	return (
		<DialogComponent.Root open={props.open}>
			{props.triggerButton && <DialogComponent.Trigger asChild>{props.triggerButton}</DialogComponent.Trigger>}
			<DialogComponent.Portal>
				<DialogComponent.Overlay onClick={() => props.setOpen?.(false)} className={overlay()} />
				<DialogComponent.Content className={base({ className: props.className })} data-brand={brand} data-theme='light'>
					<div className={modal()}>
						{layout !== 'search' && (
							<div className={close()}>
								<div className={`bg-background-primary rounded-full`}>
									<DialogComponent.Close asChild>
										{props.closeButton ?? (
											<Button
												aria-label='Close dialog'
												iconOnly={true}
												type='button'
												parent={'Popup'}
												styleSize='small'
												styleType='tertiary'
												onClick={() => props.setOpen?.(false)}
											>
												<CloseIcon />
											</Button>
										)}
									</DialogComponent.Close>
								</div>
							</div>
						)}
						{props.children}
					</div>
				</DialogComponent.Content>
			</DialogComponent.Portal>
		</DialogComponent.Root>
	);
}
